<template>
    <div class="historial-navegacion-moderno" :class="{ 'modo-compacto': compacto }">
        <!-- Header Profesional -->
        <div v-if="!compacto" class="historial-header">
            <div class="header-content">
                <div class="header-info">
                    <h3>Historial de Navegación</h3>
                    <p class="subtitulo">Total: {{ items.length }} acciones</p>
                </div>
            </div>
            <button class="btn-cerrar-elegante" @click="$emit('cerrar')">
                ×
            </button>
        </div>

        <!-- Lista de Items -->
        <div class="historial-container" :class="{ 'empty': items.length === 0 }">
            <!-- Estado vacío -->
            <div v-if="items.length === 0" class="estado-vacio">
                <p class="mensaje-vacio">No hay historial de navegación</p>
                <p class="submensaje-vacio">Las acciones aparecerán aquí mientras navegas</p>
            </div>

            <!-- Lista de items -->
            <div v-else class="historial-lista">
                <HistorialItems
v-for="(item, index) in itemsOrdenados" :key="`nav-${index}-${item.timestamp}`"
                    :item="item" @seleccionar="$emit('seleccionar', $event)" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import HistorialItems from './HistorialItems.vue';

const props = defineProps({
    items: {
        type: Array,
        default: () => []
    },
    compacto: {
        type: Boolean,
        default: false
    },
    limite: {
        type: Number,
        default: null
    }
});

defineEmits(['seleccionar', 'cerrar']);

// Computed
const itemsOrdenados = computed(() => {
    // Los items ya vienen ordenados del composable
    return props.limite ? props.items.slice(0, props.limite) : props.items;
});
</script>

<style scoped>
/* ========================================
   CONTENEDOR PRINCIPAL
======================================== */
.historial-navegacion-moderno {
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.historial-navegacion-moderno.modo-compacto {
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.06);
    background: white;
}

/* ========================================
   HEADER PROFESIONAL
======================================== */
.historial-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    position: relative;
    overflow: hidden;
}

.historial-header::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    border-radius: 50%;
    transform: translate(50%, -50%);
}

.header-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    position: relative;
    z-index: 1;
}

.header-info h3 {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 600;
    color: white;
    letter-spacing: -0.02em;
}

.subtitulo {
    margin: 0.25rem 0 0;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.8);
    font-weight: 400;
}

.btn-cerrar-elegante {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 8px;
    color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
    z-index: 1;
}

.btn-cerrar-elegante:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: scale(1.05);
}

.btn-cerrar-elegante:active {
    transform: scale(0.95);
}

/* ========================================
   CONTAINER
======================================== */
.historial-container {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    max-height: 500px;
}

.modo-compacto .historial-container {
    padding: 0.5rem;
    max-height: 350px;
}

.historial-container.empty {
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Scrollbar personalizada */
.historial-container::-webkit-scrollbar {
    width: 6px;
}

.historial-container::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.03);
    border-radius: 10px;
}

.historial-container::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, #3b82f6 0%, #1e40af 100%);
    border-radius: 10px;
}

.historial-container::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%);
}

/* ========================================
   ESTADO VACÍO
======================================== */
.estado-vacio {
    text-align: center;
    padding: 3rem 2rem;
}

.mensaje-vacio {
    margin: 0 0 0.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
}

.submensaje-vacio {
    margin: 0;
    font-size: 0.875rem;
    color: #64748b;
}

/* ========================================
   LISTA
======================================== */
.historial-lista {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}
</style>
